import type { MaybeRefOrGetter } from 'vue'
import type { Attachment } from '@corp/serializers'
import { isString } from '@corp/helpers/string'
import { useUploader, type BlobWithUrl } from '@corp/composables/uploader'

function blobToAttachment (blob: BlobWithUrl | undefined): Attachment | undefined {
  if (!blob) return

  return {
    id: blob.signed_id as any,
    url: blob.url,
    filename: blob.filename,
    byteSize: blob.byte_size,
    contentType: blob.content_type,
    createdAt: new Date(),
    signedId: blob.signed_id,
  }
}

export function useFile (fileRef?: MaybeRefOrGetter<string | Attachment | undefined>) {
  const uploader = useUploader()

  const attachment = $computed(() => {
    const file = toValue(fileRef)

    if (!file || uploader.isUploading.value)
      return undefined

    if (isString(file)) {
      const blob = uploader.lastBlob.value
      return file === blob?.signed_id ? blobToAttachment(blob) : undefined
    }

    return file
  })

  return {
    file: $$(attachment),
    filename: computed(() => attachment?.filename),
    imageUrl: computed(() => attachment?.contentType?.startsWith('image/') && attachment.url),
    ...uploader,
  }
}

export function readFileAsDataURL (file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      if (typeof reader.result === 'string')
        resolve(reader.result)
      else
        reject(new Error('Failed to read file as data URL'))
    }
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}
